
/**
 * Panel metinleri. Backend metinleri (hata mesajları, doğrulama) SUNUCUDAN
 * locale'e göre gelir — burada tekrarlanmaz (SEOAI-64/66). Burası yalnızca
 * arayüzün kendi sabit metinleridir.
 */
const MESSAGES = {
  tr: {
    'app.title': 'SEOAI Yönetim Paneli',
    'auth.email': 'E-posta',
    'auth.password': 'Parola',
    'auth.submit': 'Giriş yap',
    'auth.signingIn': 'Giriş yapılıyor…',
    'auth.logout': 'Çıkış',
    'tenant.label': 'Kurum',
    'tenant.switching': 'Kurum değiştiriliyor…',
    'tenant.loading': 'Kurumlar yükleniyor…',
    'tenant.chooseTitle': 'Kurum seçin',
    'tenant.chooseBody': 'Hangi kurumla çalışacağınızı seçin; veriler o kuruma göre gelir.',
    'tenant.noneTitle': 'Henüz bir kuruma üye değilsiniz',
    'tenant.noneBody': 'Bir kuruma davet edilmeniz gerekiyor. Kurum oluşturma platform yetkisi ister.',
    'platform.label': 'Kurum olarak görüntüle',
    'platform.hint': 'Platform yöneticisi',
    'platform.readonly': 'salt okunur',
    'platform.placeholder': 'Kurum seçin…',
    'platform.view': 'Görüntüle',
    'platform.entering': 'Kurum bağlamına geçiliyor…',
    'platform.prev': 'Önceki',
    'platform.next': 'Sonraki',
    'platform.page': '{page}/{lastPage}. sayfa · {total} kurum',
    'locale.label': 'Dil',
    'nav.label': 'Ana menü',
    'nav.projects': 'Projeler',
    'nav.roles': 'Roller',
    'roles.title': 'Roller',
    'roles.loading': 'Yükleniyor…',
    'roles.empty': 'Bu kurumda rol tanımlı değil.',
    'roles.system': 'sistem',
    'forbidden.title': 'Bu sayfaya erişiminiz yok',
    'forbidden.body': 'Bu bölümü görüntülemek için gereken izne sahip değilsiniz. Kurum yöneticinizle görüşün.',
    'forbidden.needed': 'Gereken izin:',
    'forbidden.back': 'Erişebildiğim bölüme dön',
    'landing.noAccessTitle': 'Görüntüleyebileceğiniz bir bölüm yok',
    'landing.noAccessBody': 'Bu kurumda size henüz hiçbir izin verilmemiş. Kurum yöneticinizle görüşün.',
    'permissions.loading': 'İzinler yükleniyor…',
    'projects.title': 'Projeler',
    'projects.empty': 'Bu kurumda henüz proje yok.',
    'projects.loading': 'Yükleniyor…',
    'projects.type': 'Tür',
    'projects.status': 'Durum',
    'projects.website': 'Web sitesi',
    'projects.analyses': 'Çalıştırılabilir analizler',
    'projects.back': '← Projeler',
    'error.title': 'Bir hata oluştu',
    'error.code': 'Hata kodu',
    'error.requestId': 'İstek kimliği',
    'error.retry': 'Tekrar dene',
  },
  en: {
    'app.title': 'SEOAI Admin Panel',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.submit': 'Sign in',
    'auth.signingIn': 'Signing in…',
    'auth.logout': 'Sign out',
    'tenant.label': 'Organisation',
    'tenant.switching': 'Switching organisation…',
    'tenant.loading': 'Loading organisations…',
    'tenant.chooseTitle': 'Choose an organisation',
    'tenant.chooseBody': 'Pick the organisation to work with; data is scoped to it.',
    'tenant.noneTitle': 'You are not a member of any organisation yet',
    'tenant.noneBody': 'You need to be invited to one. Creating an organisation requires a platform privilege.',
    'platform.label': 'View as organisation',
    'platform.hint': 'Platform administrator',
    'platform.readonly': 'read-only',
    'platform.placeholder': 'Choose an organisation…',
    'platform.view': 'View',
    'platform.entering': 'Entering organisation context…',
    'platform.prev': 'Previous',
    'platform.next': 'Next',
    'platform.page': 'Page {page}/{lastPage} · {total} organisations',
    'locale.label': 'Language',
    'nav.label': 'Main menu',
    'nav.projects': 'Projects',
    'nav.roles': 'Roles',
    'roles.title': 'Roles',
    'roles.loading': 'Loading…',
    'roles.empty': 'No roles defined in this organisation.',
    'roles.system': 'system',
    'forbidden.title': 'You do not have access to this page',
    'forbidden.body': 'You lack the permission required to view this section. Contact your organisation administrator.',
    'forbidden.needed': 'Required permission:',
    'forbidden.back': 'Back to a section you can access',
    'landing.noAccessTitle': 'There is no section you can view',
    'landing.noAccessBody': 'You have not been granted any permission in this organisation yet. Contact your administrator.',
    'permissions.loading': 'Loading permissions…',
    'projects.title': 'Projects',
    'projects.empty': 'No projects in this organisation yet.',
    'projects.loading': 'Loading…',
    'projects.type': 'Type',
    'projects.status': 'Status',
    'projects.website': 'Website',
    'projects.analyses': 'Available analyses',
    'projects.back': '← Projects',
    'error.title': 'Something went wrong',
    'error.code': 'Error code',
    'error.requestId': 'Request id',
    'error.retry': 'Retry',
  },
} as const;

export type MessageKey = keyof (typeof MESSAGES)['tr'];

/** Panel sözlüğünde karşılığı olan diller. Backend'de daha fazlası olabilir. */
type PanelLocale = keyof typeof MESSAGES;

const FALLBACK: PanelLocale = 'tr';

function isPanelLocale(locale: string): locale is PanelLocale {
  return locale in MESSAGES;
}

/**
 * Panel metni. Backend'de yeni bir dil açıldığında panel sözlüğü henüz o dili
 * içermeyebilir; bu durumda arayüz metinleri varsayılana düşer ama SUNUCUDAN
 * gelen metinler (hata, doğrulama) yine doğru dilde gelir — kullanıcı en
 * azından kendi dilinde hata mesajı görür. Sessiz bir bozulma değil, bilinçli
 * kademeli davranış.
 *
 * `params` verilirse `{ad}` yer tutucuları doldurulur (SEOAI-81). Cümleyi
 * parçalara bölüp JSX'te birleştirmek, dil başına farklı kelime sırasını
 * imkânsız kılardı: "3/5. sayfa" ile "Page 3/5" aynı sırayla kurulamaz.
 */
export function translate(locale: string, key: MessageKey, params?: Record<string, string | number>): string {
  const message = isPanelLocale(locale) ? MESSAGES[locale][key] : MESSAGES[FALLBACK][key];

  if (params === undefined) return message;

  return message.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}
